if (!customElements.get('load-more-products')) {
  customElements.define('load-more-products', class LoadMoreProducts extends HTMLElement {
    constructor() {
      super();
      this.button = this.querySelector('.load-more__button');
      this.noMoreButton = this.querySelector('.button--no-more');
      this.productGrid = document.querySelector('#product-grid');
      this.paginationWrapper = this.closest('.collection')?.querySelector('.pagination-wrapper');
      this.section = this.closest('[id^="shopify-section-"]');
      this.sectionId = this.section?.id.replace('shopify-section-', '');
      this.fillPageSize = Number.parseInt(this.dataset.fillPageSize, 10) || 0;
      this.spinner = null;
      this.isAutoLoading = false;
      this.isLoading = false;
    }

    connectedCallback() {
      this.button?.addEventListener('click', this.handleButtonClick.bind(this));

      if (this.fillPageSize > 0) {
        requestAnimationFrame(() => this.fillProducts(this.fillPageSize));
      } else if (this.dataset.autoLoad === 'true') {
        requestAnimationFrame(() => this.loadAllPages());
      }
    }

    async handleButtonClick() {
      if (this.fillPageSize > 0) {
        const currentProductCount = this.productGrid.querySelectorAll('.product-grid__item').length;
        await this.fillProducts(currentProductCount + this.fillPageSize);
        return;
      }

      await this.loadMore();
    }

    async fillProducts(targetProductCount) {
      if (this.isAutoLoading || !this.paginationWrapper || !this.sectionId) return;
      if (this.productGrid.querySelectorAll('.product-grid__item').length >= targetProductCount) return;

      this.isAutoLoading = true;
      this.setAttribute('aria-busy', 'true');
      this.setLoadingState(true);
      this.noMoreButton?.classList.add('hidden');

      try {
        const loadedPages = new Set();

        while (
          this.isConnected &&
          this.productGrid.querySelectorAll('.product-grid__item').length < targetProductCount
        ) {
          const nextLink = this.paginationWrapper.querySelector('.pagination__item--prev');
          const nextUrl = nextLink?.getAttribute('href');

          if (!nextUrl || loadedPages.has(nextUrl)) break;

          loadedPages.add(nextUrl);
          await this.loadMore({ manageLoadingState: false });
        }

        if (!this.paginationWrapper.querySelector('.pagination__item--prev')) {
          this.replaceWithNoMoreButton();
        }
      } catch (error) {
        console.error('Unable to fill the in-stock product grid.', error);
      } finally {
        this.setLoadingState(false);
        this.removeAttribute('aria-busy');
        this.isAutoLoading = false;
      }
    }

    async loadAllPages() {
      if (this.isAutoLoading || !this.paginationWrapper || !this.sectionId) return;

      this.isAutoLoading = true;
      this.setAttribute('aria-busy', 'true');
      this.button?.classList.add('hidden');
      this.noMoreButton?.classList.add('hidden');

      try {
        const loadedPages = new Set();

        while (this.isConnected) {
          const nextLink = this.paginationWrapper.querySelector('.pagination__item--prev');
          const nextUrl = nextLink?.getAttribute('href');

          if (!nextUrl || loadedPages.has(nextUrl)) break;

          loadedPages.add(nextUrl);
          await this.loadMore({ manageLoadingState: false });
        }

        if (this.isConnected) {
          this.classList.add('hidden');
        }
      } catch (error) {
        console.error('Unable to automatically load all in-stock products.', error);
        if (this.isConnected) {
          this.button?.classList.remove('hidden');
          this.noMoreButton?.classList.add('hidden');
        }
      } finally {
        this.removeAttribute('aria-busy');
        this.isAutoLoading = false;
      }
    }

    setLoadingState(isLoading) {
      if (!this.button) return;

      this.button.setAttribute('aria-disabled', String(isLoading));
      this.button.classList.toggle('loading', isLoading);

      if (!this.spinner) {
        this.spinner = this.button.querySelector('.loading-spinner');
      }

      this.spinner?.classList.toggle('hidden', !isLoading);
    }

    async fetchNextPage(nextUrl) {
      const url = new URL(nextUrl, window.location.origin);
      url.searchParams.set('section_id', this.sectionId);

      const response = await fetch(url.toString());
      if (!response.ok) {
        throw new Error(`Unable to load search results page (${response.status}).`);
      }

      const text = await response.text();
      return new DOMParser().parseFromString(text, 'text/html');
    }

    appendProducts(products) {
      const scrollY = window.scrollY || window.pageYOffset;

      products.forEach(product => {
        const clone = product.cloneNode(true);
        clone.style.opacity = '1';
        clone.style.transform = '';
        this.productGrid.appendChild(clone);
      });

      requestAnimationFrame(() => window.scrollTo(0, scrollY));
    }

    updatePagination(doc) {
      const newPaginationWrapper = doc.querySelector('.pagination-wrapper');
      if (!newPaginationWrapper) return false;

      this.paginationWrapper.innerHTML = newPaginationWrapper.innerHTML;

      const newPage = newPaginationWrapper.dataset.page;
      if (newPage) {
        this.paginationWrapper.dataset.page = newPage;
      }

      return !!newPaginationWrapper.querySelector('.pagination__item--prev');
    }

    replaceWithNoMoreButton() {
      this.button.classList.add('hidden');
      this.noMoreButton.classList.remove('hidden');
    }

    async loadMore({ manageLoadingState = true } = {}) {
      if (this.isLoading || !this.paginationWrapper || !this.sectionId) return;

      const nextLink = this.paginationWrapper.querySelector('.pagination__item--prev');
      if (!nextLink) return;

      const nextUrl = nextLink.getAttribute('href');
      if (!nextUrl) return;

      this.isLoading = true;
      if (manageLoadingState) this.setLoadingState(true);

      try {
        const doc = await this.fetchNextPage(nextUrl);
        if (!this.isConnected) return;

        const newProducts = doc.querySelectorAll('#product-grid .product-grid__item');

        if (newProducts.length > 0) {
          this.appendProducts(newProducts);
        }

        const hasNextPage = this.updatePagination(doc);
        if (!hasNextPage) {
          this.replaceWithNoMoreButton();
          return;
        }

      } finally {
        if (manageLoadingState) this.setLoadingState(false);
        this.isLoading = false;
      }
    }
  });
}
